// VoiceAnalystWidget.js - Enhanced with Persistent Sessions
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Button, Badge, Tooltip, Card } from 'antd';
import {
    SoundOutlined,
    SoundFilled,
    CloseOutlined,
    RobotOutlined,
    ExpandOutlined,
    CompressOutlined,
    PushpinOutlined,
    PushpinFilled
} from '@ant-design/icons';
import VoiceAnalystChat from './VoiceAnalystChat';

const VoiceAnalystWidget = ({
                                onAnalystResponse = null,
                                context = null,
                                showCallModal = null,
                                showReportModal = null,
                                showCallsList = null,
                                initialMessage = "Hi! I'm your Call Analytics Expert. I can analyze your call data and provide insights. What would you like to know?"
                            }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [sessionStarted, setSessionStarted] = useState(false);
    const [audioEnabled, setAudioEnabled] = useState(false); // Start with audio disabled
    const [connectionStatus, setConnectionStatus] = useState('disconnected');
    const [hasNewMessages, setHasNewMessages] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [isPinned, setIsPinned] = useState(true);
    const [position, setPosition] = useState({ bottom: 24, right: 24 });
    const [panelSize, setPanelSize] = useState({ width: 380, height: 500 });
    const [isClosing, setIsClosing] = useState(false);

    const panelRef = useRef(null);
    const resizeRef = useRef(null);

    const handleToggle = useCallback(() => {
        if (isOpen) {
            // Just close the UI, keep session alive
            setIsClosing(true);
            setTimeout(() => {
                setIsOpen(false);
                setIsExpanded(false);
                setIsClosing(false);
                setAudioEnabled(false); // Reset audio when closing UI
            }, 200);
        } else {
            // Open the UI and start session if not already started
            setIsOpen(true);
            if (!sessionStarted) {
                setSessionStarted(true);
            }
            setAudioEnabled(false); // Start with audio disabled
        }
    }, [isOpen, sessionStarted]);

    // New function to end the session completely
    const handleEndSession = useCallback(() => {
        setSessionStarted(false);
        setIsOpen(false);
        setIsExpanded(false);
        setAudioEnabled(false);
        setConnectionStatus('disconnected');
        setHasNewMessages(false);
    }, []);

    // Handle pin/unpin toggle
    const handlePinToggle = useCallback(() => {
        setIsPinned(!isPinned);
        if (!isPinned) {
            // When pinning, move back to default position
            setPosition({ bottom: 24, right: 24 });
        }
    }, [isPinned]);

    const handleExpand = useCallback(() => {
        setIsExpanded(!isExpanded);
        if (!isExpanded) {
            setPanelSize({ width: 500, height: 700 });
        } else {
            setPanelSize({ width: 380, height: 500 });
        }
    }, [isExpanded]);

    const handleAudioToggle = useCallback(() => {
        setAudioEnabled(!audioEnabled);
    }, [audioEnabled]);

    const handleConnectionStatusChange = useCallback((status) => {
        setConnectionStatus(status);
    }, []);

    const handleAnalystResponse = useCallback((response) => {
        if (!isOpen) {
            setHasNewMessages(true);
        }
        if (onAnalystResponse) {
            onAnalystResponse(response);
        }
    }, [onAnalystResponse, isOpen]);

    // Reset new messages when chat is opened
    useEffect(() => {
        if (isOpen) {
            setHasNewMessages(false);
        }
    }, [isOpen]);

    const getConnectionStatusColor = () => {
        switch (connectionStatus) {
            case 'connected': return '#52c41a';
            case 'connecting': return '#faad14';
            case 'error': return '#ff4d4f';
            default: return '#d9d9d9';
        }
    };

    // Drag functionality - works when unpinned (for both icon and panel)
    const handleMouseDown = useCallback((e) => {
        if (isPinned) return; // Don't allow dragging when pinned

        setIsDragging(true);
        const startX = e.clientX;
        const startY = e.clientY;

        // Convert current bottom/right position to top/left for easier calculation
        const currentLeft = window.innerWidth - position.right - (isOpen ? panelSize.width : 56);
        const currentTop = window.innerHeight - position.bottom - (isOpen ? panelSize.height : 56);

        const handleMouseMove = (e) => {
            // Calculate movement delta in normal screen coordinates
            const deltaX = e.clientX - startX;
            const deltaY = e.clientY - startY;

            // Calculate new top/left position
            const newLeft = currentLeft + deltaX;
            const newTop = currentTop + deltaY;

            // Apply boundary constraints
            const widgetWidth = isOpen ? panelSize.width : 56;
            const widgetHeight = isOpen ? panelSize.height : 56;

            const constrainedLeft = Math.max(24, Math.min(window.innerWidth - widgetWidth - 24, newLeft));
            const constrainedTop = Math.max(24, Math.min(window.innerHeight - widgetHeight - 24, newTop));

            // Convert back to bottom/right for CSS positioning
            const newRight = window.innerWidth - constrainedLeft - widgetWidth;
            const newBottom = window.innerHeight - constrainedTop - widgetHeight;

            setPosition({ right: newRight, bottom: newBottom });
        };

        const handleMouseUp = () => {
            setIsDragging(false);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, [isPinned, position, isOpen, panelSize]);

    // Panel resize functionality
    const handleResizeMouseDown = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();

        const startX = e.clientX;
        const startY = e.clientY;
        const startWidth = panelSize.width;
        const startHeight = panelSize.height;

        const handleMouseMove = (e) => {
            const newWidth = Math.max(320, startWidth - (e.clientX - startX));
            const newHeight = Math.max(400, startHeight - (e.clientY - startY));
            setPanelSize({ width: newWidth, height: newHeight });
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, [panelSize]);

    const floatingIconStyle = {
        position: 'fixed',
        right: position.right,
        bottom: position.bottom,
        zIndex: 1000,
        cursor: isDragging ? 'grabbing' : (isPinned ? 'pointer' : 'grab'),
        transition: isDragging ? 'none' : 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: `scale(${isDragging ? 1.05 : 1})`,
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
    };

    const panelStyle = {
        position: 'fixed',
        right: isPinned ? 24 : position.right,
        bottom: isPinned ? 24 : position.bottom,
        width: panelSize.width,
        height: panelSize.height,
        zIndex: 1001,
        borderRadius: '12px',
        boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
        transition: isDragging ? 'none' : (isClosing ? 'all 0.2s ease-in' : 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'),
        transform: isClosing ? 'scale(0.95) translateY(10px)' : 'scale(1) translateY(0)',
        opacity: isClosing ? 0 : 1,
        overflow: 'hidden',
        cursor: isDragging ? 'grabbing' : (isPinned ? 'default' : 'grab'),
    };

    const headerStyle = {
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        padding: '12px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        cursor: isPinned ? 'default' : 'grab',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
    };

    return (
        <>
            {/* Floating Icon */}
            {!isOpen && (
                <div style={floatingIconStyle}>
                    <Tooltip
                        title={
                            <div>
                                <div>AI Analyst</div>
                                {sessionStarted && <div style={{fontSize: '11px', opacity: 0.8}}>Session active</div>}
                                {!isPinned && <div style={{fontSize: '11px', opacity: 0.8}}>Drag to move</div>}
                            </div>
                        }
                        placement="left"
                    >
                        <Badge dot={hasNewMessages} offset={[-8, 8]}>
                            <Button
                                type="primary"
                                shape="circle"
                                size="large"
                                icon={<RobotOutlined style={{ fontSize: '18px' }} />}
                                onClick={handleToggle}
                                onMouseDown={handleMouseDown}
                                style={{
                                    width: 56,
                                    height: 56,
                                    border: 'none',
                                    background: sessionStarted
                                        ? 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)'
                                        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            />
                        </Badge>
                    </Tooltip>
                </div>
            )}

            {/* Chat Panel - Always render once session is started */}
            {sessionStarted && (
                <Card style={{...panelStyle, display: isOpen ? 'block' : 'none'}} bodyStyle={{ padding: 0, height: '100%' }}>
                    {/* Resize Handle */}
                    <div
                        onMouseDown={handleResizeMouseDown}
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '12px',
                            height: '12px',
                            cursor: 'nw-resize',
                            background: 'transparent',
                            zIndex: 10,
                        }}
                    />

                    {/* Header - make it draggable when unpinned */}
                    <div
                        style={headerStyle}
                        onMouseDown={!isPinned ? handleMouseDown : undefined}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <RobotOutlined />
                            <span style={{ fontWeight: 500 }}>AI Analyst</span>
                            <div
                                style={{
                                    width: '6px',
                                    height: '6px',
                                    borderRadius: '50%',
                                    backgroundColor: getConnectionStatusColor(),
                                }}
                            />
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Tooltip title={audioEnabled ? "Disable Voice" : "Enable Voice"}>
                                <Button
                                    type="text"
                                    size="small"
                                    icon={audioEnabled ? <SoundFilled /> : <SoundOutlined />}
                                    onClick={handleAudioToggle}
                                    disabled={connectionStatus !== 'connected'}
                                    style={{ color: 'white', opacity: audioEnabled ? 1 : 0.6 }}
                                />
                            </Tooltip>

                            {/* Pin/Unpin Button */}
                            <Tooltip title={isPinned ? "Unpin (allow dragging)" : "Pin to corner"}>
                                <Button
                                    type="text"
                                    size="small"
                                    icon={isPinned ? <PushpinFilled /> : <PushpinOutlined />}
                                    onClick={handlePinToggle}
                                    style={{ color: 'white' }}
                                />
                            </Tooltip>

                            <Tooltip title={isExpanded ? "Shrink" : "Expand"}>
                                <Button
                                    type="text"
                                    size="small"
                                    icon={isExpanded ? <CompressOutlined /> : <ExpandOutlined />}
                                    onClick={handleExpand}
                                    style={{ color: 'white' }}
                                />
                            </Tooltip>

                            <Tooltip title="Close (session continues)">
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<CloseOutlined />}
                                    onClick={handleToggle}
                                    style={{ color: 'white' }}
                                />
                            </Tooltip>
                        </div>
                    </div>

                    {/* Chat Content */}
                    <div style={{ height: 'calc(100% - 48px)' }}>
                        <VoiceAnalystChat
                            sessionStarted={sessionStarted}
                            isVisible={isOpen}
                            audioEnabled={audioEnabled}
                            onConnectionStatusChange={handleConnectionStatusChange}
                            onAnalystResponse={handleAnalystResponse}
                            onEndSession={handleEndSession}
                            context={context}
                            initialMessage={initialMessage}
                            height="100%"
                            showCallModal={showCallModal}
                            showReportModal={showReportModal}
                            showCallsList={showCallsList}
                        />
                    </div>
                </Card>
            )}
        </>
    );
};

export default VoiceAnalystWidget;
